import { useEffect, useRef, useState } from 'react'
import type { User } from 'firebase/auth'
import { addDoc, collection, doc, getDoc, getDocs, getFirestore, limit, query, serverTimestamp, updateDoc, where } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { app, storage } from '../../firebase'
import { translations, type Language } from '../../translations'
import { TRIAL_DAILY_LIMIT, TRIAL_DURATION_DAYS } from './constants'

interface AdminSettings {
    bankName: string
    accountName: string
    iban: string
    swift: string
    rib: string
    paymentInstructions: string
    contactEmail: string
    contactPhone: string
    whatsapp: string
    officeAddress: string
    premiumPlanPrice: number
    enabledPaymentMethods: {
        bankTransfer: boolean
        invoiceRequest: boolean
    }
}

const DEFAULT_SETTINGS: AdminSettings = {
    bankName: '',
    accountName: '',
    iban: '',
    swift: '',
    rib: '',
    paymentInstructions: '',
    contactEmail: '',
    contactPhone: '',
    whatsapp: '',
    officeAddress: '',
    premiumPlanPrice: 0,
    enabledPaymentMethods: {
        bankTransfer: true,
        invoiceRequest: false
    }
}

function normalizeAdminSettings(raw?: (Partial<AdminSettings> & Record<string, unknown>)): AdminSettings {
    const merged = { ...DEFAULT_SETTINGS, ...(raw || {}) } as AdminSettings
    const priceSource = raw?.premiumPlanPrice ?? merged.premiumPlanPrice
    let premiumPlanPrice = DEFAULT_SETTINGS.premiumPlanPrice
    if (typeof priceSource === 'number' && Number.isFinite(priceSource)) {
        premiumPlanPrice = priceSource
    } else if (typeof priceSource === 'string') {
        const parsed = Number(priceSource)
        premiumPlanPrice = Number.isFinite(parsed) ? parsed : DEFAULT_SETTINGS.premiumPlanPrice
    }

    return {
        ...merged,
        rib: typeof merged.rib === 'string' ? merged.rib : '',
        premiumPlanPrice,
        enabledPaymentMethods: {
            bankTransfer: merged.enabledPaymentMethods?.bankTransfer ?? DEFAULT_SETTINGS.enabledPaymentMethods.bankTransfer,
            invoiceRequest: merged.enabledPaymentMethods?.invoiceRequest ?? DEFAULT_SETTINGS.enabledPaymentMethods.invoiceRequest
        }
    }
}

interface UpgradeModalProps {
    isOpen: boolean
    onClose: () => void
    user: User | null
    lang: Language
}

type Step = 'info' | 'method_selection' | 'upload' | 'pending' | 'invoice_form'

interface OrderRecord {
    id: string
    status: 'pending_payment' | 'pending_approval' | 'invoice_requested'
    createdAt?: unknown
    uploadedAt?: unknown
    paymentMethod?: string
}

interface TrialInfo {
    trialActive: boolean
    trialDaysRemaining: number
}

export default function UpgradeModal({ isOpen, onClose, user, lang }: UpgradeModalProps) {
    const t = translations[lang]
    const isRTL = lang === 'ar'

    const [step, setStep] = useState<Step>('info')
    const [loading, setLoading] = useState(false)
    const [orderId, setOrderId] = useState<string | null>(null)
    const [uploadProgress, setUploadProgress] = useState(false)
    const [existingOrder, setExistingOrder] = useState<OrderRecord | null>(null)
    const [checkingOrder, setCheckingOrder] = useState(false)
    const [adminSettings, setAdminSettings] = useState<AdminSettings>(DEFAULT_SETTINGS)
    const [invoiceForm, setInvoiceForm] = useState({
        contactName: '',
        companyName: '',
        address: '',
        phone: '',
        userCount: 1,
        message: ''
    })
    const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null)
    const [settingsLoading, setSettingsLoading] = useState(true)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const db = getFirestore(app, 'dxt-law-db')

    useEffect(() => {
        if (!isOpen) {
            setStep('info')
            setOrderId(null)
            setExistingOrder(null)
            setUploadProgress(false)
            setSettingsLoading(true)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen || !user) return

        let cancelled = false

        const fetchExistingOrder = async () => {
            setCheckingOrder(true)
            try {
                const q = query(collection(db, 'orders'), where('userId', '==', user.uid), limit(5))
                const snapshot = await getDocs(q)
                if (cancelled) return

                const match = snapshot.docs.find((docSnap) => {
                    const data = docSnap.data()
                    return data.status === 'pending_payment' || data.status === 'pending_approval'
                })

                if (match) {
                    const data = match.data() as Omit<OrderRecord, 'id'>
                    const normalized: OrderRecord = {
                        id: match.id,
                        status: data.status,
                        createdAt: data.createdAt,
                        uploadedAt: data.uploadedAt
                    }
                    setExistingOrder(normalized)
                    setOrderId(match.id)
                    if (data.status === 'pending_payment') {
                        setStep('upload')
                    } else if (data.status === 'invoice_requested') {
                        setStep('pending')
                    } else {
                        setStep('pending')
                    }
                } else {
                    setExistingOrder(null)
                    setOrderId(null)
                    setStep('info')
                }
            } catch (error) {
                console.error('Error checking existing orders:', error)
            } finally {
                if (!cancelled) {
                    setCheckingOrder(false)
                }
            }
        }

        fetchExistingOrder()
        return () => {
            cancelled = true
        }
    }, [db, isOpen, user])

    // Fetch trial info for trial-specific messaging
    useEffect(() => {
        if (!isOpen || !user) return
        let cancelled = false

        const fetchTrialInfo = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid))
                if (cancelled) return

                if (!userDoc.exists()) {
                    setTrialInfo({ trialActive: true, trialDaysRemaining: 5 })
                    return
                }

                const data = userDoc.data()
                // Use subscriptionEnd if available (source of truth)
                const subscriptionEnd = data.subscriptionEnd

                if (subscriptionEnd) {
                    const endDate = subscriptionEnd.toDate()
                    const now = new Date()
                    const msRemaining = endDate.getTime() - now.getTime()
                    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24))

                    setTrialInfo({
                        trialActive: daysRemaining > 0,
                        trialDaysRemaining: Math.max(0, daysRemaining),
                    })
                } else {
                    // Fallback to createdAt if subscriptionEnd is missing (legacy support)
                    const createdAt = data.createdAt || data.created_at
                    if (createdAt) {
                        const created = new Date(createdAt)
                        const now = new Date()
                        const daysSinceSignup = Math.floor(
                            (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
                        )
                        setTrialInfo({
                            trialActive: daysSinceSignup < TRIAL_DURATION_DAYS,
                            trialDaysRemaining: Math.max(0, TRIAL_DURATION_DAYS - daysSinceSignup),
                        })
                    } else {
                        setTrialInfo({ trialActive: false, trialDaysRemaining: 0 })
                    }
                }
            } catch (error) {
                console.error('Error fetching trial info:', error)
            }
        }

        fetchTrialInfo()
        return () => {
            cancelled = true
        }
    }, [db, isOpen, user])

    useEffect(() => {
        if (!isOpen) return
        let cancelled = false
        const fetchSettings = async () => {
            setSettingsLoading(true)
            try {
                const ref = doc(db, 'admin_settings', 'app')
                const snapshot = await getDoc(ref)
                if (cancelled) return
                if (snapshot.exists()) {
                    const raw = snapshot.data() as Partial<AdminSettings> & Record<string, unknown>
                    setAdminSettings(normalizeAdminSettings(raw))
                } else {
                    setAdminSettings(DEFAULT_SETTINGS)
                }
            } catch (error) {
                console.error('Error fetching admin settings:', error)
                if (!cancelled) {
                    setAdminSettings(DEFAULT_SETTINGS)
                }
            } finally {
                if (!cancelled) {
                    setSettingsLoading(false)
                }
            }
        }

        fetchSettings()
        return () => {
            cancelled = true
        }
    }, [db, isOpen])

    const locale = lang === 'ar' ? 'ar-MA' : 'fr-MA'
    const priceSuffix = lang === 'ar' ? 'درهم / شهر' : 'DH / Mois'
    const displayPrice = adminSettings.premiumPlanPrice > 0
        ? `${adminSettings.premiumPlanPrice.toLocaleString(locale)} ${priceSuffix}`
        : t.priceValue
    const fallbackBankName = adminSettings.bankName?.trim() || '—'
    const fallbackBeneficiary = adminSettings.accountName?.trim() || '—'
    const fallbackRib = adminSettings.rib?.trim() || '—'
    const fallbackIban = adminSettings.iban?.trim()
    const fallbackSwift = adminSettings.swift?.trim()
    const paymentInstructions = adminSettings.paymentInstructions?.trim()
    const contactEmail = adminSettings.contactEmail?.trim()
    const contactPhone = adminSettings.contactPhone?.trim()
    const contactWhatsapp = adminSettings.whatsapp?.trim()
    const premiumPlanAmount = adminSettings.premiumPlanPrice > 0 ? adminSettings.premiumPlanPrice : 200

    if (!isOpen) return null

    const handleStartUpgrade = () => {
        if (!user) return
        if (existingOrder) {
            if (existingOrder.status === 'pending_payment') {
                setStep('upload')
            } else {
                setStep('pending')
            }
            return
        }

        const methods = adminSettings.enabledPaymentMethods
        if (methods.bankTransfer && methods.invoiceRequest) {
            setStep('method_selection')
        } else if (methods.invoiceRequest) {
            setStep('invoice_form')
        } else {
            // Default to bank transfer
            handleCreateBankOrder()
        }
    }

    const handleCreateBankOrder = async () => {
        if (!user) return
        setLoading(true)
        try {
            const orderRef = await addDoc(collection(db, 'orders'), {
                userId: user.uid,
                userEmail: user.email,
                status: 'pending_payment',
                amount: premiumPlanAmount,
                currency: 'MAD',
                plan: 'premium_monthly',
                paymentMethod: 'bank_transfer',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            })
            setOrderId(orderRef.id)
            setExistingOrder({ id: orderRef.id, status: 'pending_payment', paymentMethod: 'bank_transfer' })
            setStep('upload')
        } catch (error) {
            console.error('Error creating order:', error)
            alert(t.errorGeneric)
        } finally {
            setLoading(false)
        }
    }

    const handleInvoiceSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user) return
        setLoading(true)
        try {
            const orderRef = await addDoc(collection(db, 'orders'), {
                userId: user.uid,
                userEmail: user.email,
                status: 'invoice_requested',
                plan: 'premium_monthly', // or custom
                paymentMethod: 'invoice_request',
                invoiceDetails: invoiceForm,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            })
            setOrderId(orderRef.id)
            setExistingOrder({ id: orderRef.id, status: 'invoice_requested', paymentMethod: 'invoice_request' })
            setStep('pending')
        } catch (error) {
            console.error('Error creating invoice request:', error)
            alert(t.errorGeneric)
        } finally {
            setLoading(false)
        }
    }

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file || !user || !orderId) return

        setUploadProgress(true)
        try {
            const fileRef = ref(storage, `users/${user.uid}/receipts/${orderId}_${file.name}`)
            await uploadBytes(fileRef, file)
            const downloadURL = await getDownloadURL(fileRef)

            const orderRef = doc(db, 'orders', orderId)
            await updateDoc(orderRef, {
                receiptUrl: downloadURL,
                receiptPath: fileRef.fullPath,
                status: 'pending_approval',
                uploadedAt: serverTimestamp()
            })

            setExistingOrder((prev) => (prev ? { ...prev, status: 'pending_approval' } : prev))
            setStep('pending')
        } catch (error) {
            console.error('Error uploading receipt:', error)
            alert(t.errorGeneric)
        } finally {
            setUploadProgress(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl animate-in zoom-in-95 duration-200 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="mb-6 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900">
                        {step === 'info' && t.upgradeTitle}
                        {step === 'method_selection' && (lang === 'ar' ? 'طريقة الدفع' : 'Mode de paiement')}
                        {step === 'invoice_form' && (lang === 'ar' ? 'طلب فاتورة' : 'Demander une facture')}
                        {step === 'upload' && t.upgradeStep3}
                        {(step === 'pending' && existingOrder?.status === 'invoice_requested') && (lang === 'ar' ? 'تم إرسال الطلب' : 'Demande envoyée')}
                        {(step === 'pending' && existingOrder?.status !== 'invoice_requested') && t.orderCreated}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {step === 'info' && (
                    <div className="space-y-6">
                        {checkingOrder && (
                            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                <svg className="h-4 w-4 animate-spin text-slate-500" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t.checkingOrders}
                            </div>
                        )}

                        {/* Simplified Info Layout */}
                        <div className="space-y-4">
                            {/* Trial status banner */}
                            {trialInfo && trialInfo.trialActive && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                                    <div className="flex items-center gap-2 text-amber-800">
                                        <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-medium">
                                            {trialInfo.trialDaysRemaining === 1
                                                ? t.trialBannerLastDay
                                                : t.trialBanner.replace('{days}', String(trialInfo.trialDaysRemaining))}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-sm text-amber-700">{t.trialKeepAccess}</p>
                                </div>
                            )}

                            {trialInfo && !trialInfo.trialActive && (
                                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                                    <div className="flex items-center gap-2 text-red-800">
                                        <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span className="font-medium">{t.trialEnded}</span>
                                    </div>
                                    <p className="mt-2 text-sm text-red-700">{t.trialKeepAccess}</p>
                                </div>
                            )}

                            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="font-semibold text-blue-900">Premium Plan</span>
                                    <span className="font-bold text-blue-700">{settingsLoading ? t.chatThinking : displayPrice}</span>
                                </div>
                                <p className="text-sm text-blue-800 opacity-80">{t.planBenefits}</p>
                                <p className="mt-2 text-xs text-blue-700 opacity-80">
                                    {TRIAL_DAILY_LIMIT} {lang === 'ar' ? 'عملية بحث يومياً خلال التجربة المجانية' : 'recherches/jour pendant l\'essai gratuit'}
                                </p>
                            </div>

                            {(contactEmail || contactPhone || contactWhatsapp) && (
                                <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        {lang === 'ar' ? 'الدعم والتواصل' : 'Support & contact'}
                                    </p>
                                    <div className="mt-2 space-y-2">
                                        {contactEmail && (
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Email</span>
                                                <a href={`mailto:${contactEmail}`} className="font-medium text-blue-700 hover:underline">
                                                    {contactEmail}
                                                </a>
                                            </div>
                                        )}
                                        {contactPhone && (
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">{lang === 'ar' ? 'هاتف' : 'Téléphone'}</span>
                                                <a href={`tel:${contactPhone}`} className="font-medium text-slate-900">
                                                    {contactPhone}
                                                </a>
                                            </div>
                                        )}
                                        {contactWhatsapp && (
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">WhatsApp</span>
                                                <a href={`https://wa.me/${contactWhatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="font-medium text-slate-900">
                                                    {contactWhatsapp}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleStartUpgrade}
                            disabled={loading || checkingOrder}
                            className="w-full rounded-xl bg-blue-600 py-3 px-4 font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? t.chatThinking : t.upgradeConfirm}
                        </button>
                    </div>
                )}

                {step === 'method_selection' && (
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600 mb-4">{lang === 'ar' ? 'يرجى اختيار طريقة الدفع المناسبة:' : 'Veuillez choisir votre mode de paiement :'}</p>

                        <button
                            onClick={handleCreateBankOrder}
                            className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 transition-all hover:bg-slate-50 hover:shadow-md"
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">🏦</span>
                                <div className="text-left">
                                    <p className="font-semibold text-slate-900">{lang === 'ar' ? 'تحويل بنكي' : 'Virement Bancaire'}</p>
                                    <p className="text-xs text-slate-500">{lang === 'ar' ? 'إرفاق إيصال الدفع' : 'Joindre le reçu de paiement'}</p>
                                </div>
                            </div>
                            <svg className={`h-5 w-5 text-slate-400 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </button>

                        <button
                            onClick={() => setStep('invoice_form')}
                            className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 transition-all hover:bg-slate-50 hover:shadow-md"
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">📄</span>
                                <div className="text-left">
                                    <p className="font-semibold text-slate-900">{lang === 'ar' ? 'طلب فاتورة' : 'Demander une facture'}</p>
                                    <p className="text-xs text-slate-500">{lang === 'ar' ? 'للدفع لاحقا (شركات)' : 'Paiement sur facture (Entreprises)'}</p>
                                </div>
                            </div>
                            <svg className={`h-5 w-5 text-slate-400 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                )}

                {step === 'invoice_form' && (
                    <form onSubmit={handleInvoiceSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="text-xs font-semibold text-slate-700">{lang === 'ar' ? 'جهة الاتصال' : 'Nom du contact'}</span>
                                <input
                                    required
                                    type="text"
                                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                    value={invoiceForm.contactName}
                                    onChange={e => setInvoiceForm({ ...invoiceForm, contactName: e.target.value })}
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-slate-700">{lang === 'ar' ? 'الهاتف' : 'Téléphone'}</span>
                                <input
                                    required
                                    type="tel"
                                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                    value={invoiceForm.phone}
                                    onChange={e => setInvoiceForm({ ...invoiceForm, phone: e.target.value })}
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-700">{lang === 'ar' ? 'اسم الشركة / المؤسسة' : 'Nom de l\'entreprise / Organisation'}</span>
                            <input
                                required
                                type="text"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={invoiceForm.companyName}
                                onChange={e => setInvoiceForm({ ...invoiceForm, companyName: e.target.value })}
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-700">{lang === 'ar' ? 'العنوان' : 'Adresse de facturation'}</span>
                            <input
                                required
                                type="text"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={invoiceForm.address}
                                onChange={e => setInvoiceForm({ ...invoiceForm, address: e.target.value })}
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-700">{lang === 'ar' ? 'عدد المستخدمين' : 'Nombre d\'utilisateurs'}</span>
                            <input
                                type="number"
                                min="1"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={invoiceForm.userCount}
                                onChange={e => setInvoiceForm({ ...invoiceForm, userCount: parseInt(e.target.value) || 1 })}
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-700">{lang === 'ar' ? 'رسالة (اختياري)' : 'Message (Optionnel)'}</span>
                            <textarea
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm h-20"
                                value={invoiceForm.message}
                                onChange={e => setInvoiceForm({ ...invoiceForm, message: e.target.value })}
                            />
                        </label>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-xl bg-blue-600 py-3 px-4 font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? t.chatThinking : (lang === 'ar' ? 'إرسال الطلب' : 'Envoyer la demande')}
                        </button>
                    </form>
                )}

                {step === 'upload' && (
                    <div className="space-y-6 text-center">
                        {existingOrder && existingOrder.status === 'pending_payment' && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
                                <p className="text-sm font-semibold text-amber-900">{t.pendingOrderTitle}</p>
                                <p className="mt-1 text-sm text-amber-800">{t.pendingOrderNotice}</p>
                                <p className="mt-2 text-xs font-mono text-amber-700">#{existingOrder.id}</p>
                            </div>
                        )}

                        {/* Bank Details & Instructions */}
                        <div className="text-left space-y-4 mb-6">
                            {paymentInstructions && (
                                <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        {lang === 'ar' ? 'تعليمات الدفع' : 'Instructions de paiement'}
                                    </p>
                                    <p className="mt-2 whitespace-pre-line text-slate-700">{paymentInstructions}</p>
                                </div>
                            )}

                            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{t.bankDetails}</h4>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">{t.bankName}</span>
                                    <span className="font-medium text-slate-900">{fallbackBankName}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">{t.bankBeneficiary}</span>
                                    <span className="font-medium text-slate-900">{fallbackBeneficiary}</span>
                                </div>
                                <div className="border-t border-slate-200 pt-2">
                                    <span className="mb-1 block text-slate-500">{t.bankAccount}</span>
                                    <code className="block select-all rounded border border-slate-200 bg-white p-2 text-center font-mono text-xs">
                                        {fallbackRib}
                                    </code>
                                </div>
                                {fallbackIban && (
                                    <div className="border-t border-slate-200 pt-2">
                                        <span className="mb-1 block text-slate-500">IBAN</span>
                                        <code className="block select-all rounded border border-slate-200 bg-white p-2 text-center font-mono text-xs">
                                            {fallbackIban}
                                        </code>
                                    </div>
                                )}
                                {fallbackSwift && (
                                    <div className="border-t border-slate-200 pt-2">
                                        <span className="mb-1 block text-slate-500">SWIFT / BIC</span>
                                        <code className="block select-all rounded border border-slate-200 bg-white p-2 text-center font-mono text-xs">
                                            {fallbackSwift}
                                        </code>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                            </svg>
                        </div>
                        <p className="text-slate-600">{t.uploadHint}</p>

                        <input ref={fileInputRef} type="file" onChange={handleFileUpload} accept="image/*,.pdf" className="hidden" />

                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadProgress}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 px-4 font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
                        >
                            {uploadProgress ? (
                                <>
                                    <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t.uploading}
                                </>
                            ) : (
                                t.uploadButton
                            )}
                        </button>
                    </div>
                )}

                {step === 'pending' && (
                    <div className="space-y-6 text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </div>
                        <div className="space-y-2">
                            <p className="font-medium text-slate-600">
                                {existingOrder?.status === 'invoice_requested'
                                    ? (lang === 'ar' ? 'تم استلام طلبك بنجاح' : 'Demande reçue avec succès')
                                    : t.pendingApproval}
                            </p>
                            {existingOrder && (
                                <p className="text-sm text-slate-500">
                                    {existingOrder.status === 'invoice_requested'
                                        ? (lang === 'ar' ? 'سيتم التواصل معكم قريبا لترتيب الفاتورة وتفعيل الحساب.' : 'Nous vous contacterons bientôt pour finaliser la facture et l\'activation.')
                                        : t.pendingOrderReview}
                                </p>
                            )}
                        </div>
                        <button onClick={onClose} className="w-full rounded-xl bg-slate-100 py-3 px-4 font-bold text-slate-900 transition-colors hover:bg-slate-200">
                            {t.upgradeCancel}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
